/**
 * Ant Design's date-picker popup renders to document.body at z-index ~1050
 * by default. FetchModal stacks at 99999+; when a picker lives inside a
 * stacked modal (e.g. the RFP edit modal at 100010), the popup gets hidden
 * behind the modal. Push it above every reasonable modal layer here.
 */
export const PICKER_POPUP_Z_INDEX = 100050;

/** Radix menu/popover content motion used by account menus and switchers. */
export const MENU_CONTENT_MOTION_CLASS =
  "data-[state=open]:data-[side=top]:animate-slideUpAndFade " +
  "data-[state=open]:data-[side=right]:animate-slideRightAndFade " +
  "data-[state=open]:data-[side=left]:animate-slideLeftAndFade " +
  "data-[state=open]:data-[side=bottom]:animate-slideDownAndFade " +
  "data-[state=closed]:animate-fadeOut";

/**
 * Vertical gap (px) between a kebab trigger's bottom edge and the menu it opens. A kebab's
 * left-click dispatches a synthetic `contextmenu` event at this offset below itself, so the reused
 * right-click menu renders just under the button.
 *
 * Lives here rather than in a feature: the proposals grid, the sourcing search and the admin
 * venues table each open the same kebab, and three copies of the number is how they drift apart.
 */
export const KEBAB_MENU_OFFSET_PX = 4;

/**
 * Shared class list for the 44x44px icon buttons that sit in a table's Actions column (a kebab, a
 * "View quote" icon). They sit side by side, so any drift between them is immediately visible;
 * 44px is the a11y touch-target minimum.
 */
export const TABLE_ICON_BUTTON_CLASS =
  "flex h-11 w-11 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900";

/**
 * The backdrop layer a `FetchModal` sits on unless its caller says otherwise. Its panel content
 * renders one above this.
 *
 * Lives here rather than in `FetchModal.tsx` because that file is `"use client"`, and a server
 * module that needs the number would drag the whole component (framer-motion included) into the
 * server graph to read it.
 */
export const FETCH_MODAL_DEFAULT_Z_INDEX = 99999;

/**
 * The layer anything opened from inside a portaled overlay has to reach: one above a
 * `FetchModal`'s panel content, which is itself one above {@link FETCH_MODAL_DEFAULT_Z_INDEX}.
 *
 * Used by a stacked modal that takes a numeric `zIndex` prop. {@link DIALOG_ABOVE_OVERLAY_Z_CLASS}
 * is the Tailwind-class form of this same layer; the two must agree.
 */
export const ABOVE_OVERLAY_Z_INDEX = FETCH_MODAL_DEFAULT_Z_INDEX + 2;

/**
 * `zIndexClass` for a `FetchConfirmationDialog` opened from inside a portaled overlay - a
 * `FetchModal` or a `SlideOverPanel` (z-[9998]/z-[9999]).
 *
 * The dialog's own default is `z-50`, which puts it UNDER either of those: the confirm never
 * becomes visible and the action it guards appears to do nothing. Anything rendered inside one of
 * those overlays must pass this.
 *
 * Written out rather than built from {@link ABOVE_OVERLAY_Z_INDEX}: Tailwind's scanner only sees
 * class names that appear literally in the source, so an interpolated one produces no CSS. Keep
 * the two in step by hand; they are declared together for exactly that reason.
 */
export const DIALOG_ABOVE_OVERLAY_Z_CLASS = "z-[100001]";
